/* eslint-disable @typescript-eslint/no-explicit-any */
import Download from '@src/lib/download';

/**
 * 详情页 `FJDetailsPage`：对外 action 入口。
 * 统一通过 `FJDetailsPageAction` 访问 thunk；。
 */
import FJConstant from '../../constants/FJConstant';
import FJConfig from '../../config/FJConfig';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import FJExportStore from '../../store/FJExportStore';
import FJProjectStore from '../../store/FJProjectStore';
import FJLocalStore from '../../store/FJLocalStore';
import type { AppThunk } from '../appThunk';
import { FJModalsAction } from './FJModalsAction';
import { resetProjectsListPage, setProjects } from '../slice/FJStartLandingSlice';
import {
    hideDetailsPage,
    removeDetailsItem,
    renameDetailsItem,
    retryDetailsItem,
    selectDetailsItem,
    setDetailsItems,
    setDetailsItemStatus,
    setDetailsProjectTitle,
    setDetailsSelectedClipIds as setDetailsSelectedClipIdsSlice,
    toggleDetailsClipSelected as toggleDetailsClipSelectedSlice,
    clearDetailsClipSelection as clearDetailsClipSelectionSlice,
    toggleDetailsItemDisliked,
    toggleDetailsItemLiked,
    updateDetailsItem,
    updateDetailsItemSubEventId,
    type FJDetailsListItemSuccess,
    type FJDetailsSubtitleItem,
} from '../slice/FJDetailsPageSlice';
import { FJ_COMMON_VIEW, setActiveView } from '../slice/FJCommonSlice';
import FJUtil from '@src/util/FJUtil';
import FJLongToShortStore from '@src/store/FJLongToShortStore';
import FJBrowserHistoryStore from '@src/store/FJBrowserHistoryStore';
import FJLongToShortUpdateStore from '@src/store/FJLongToShortUpdateStore';
import type { ProjectItem } from '../../types/FJProject';
import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import type { RootState } from '../index';
import FJTrimAction from '@src/redux/action/FJTrimAction.ts';
import FJCoreConfig from '@src/core/FJCoreConfig';
import { FJCommonAction } from './FJCommonAction';
import FJPublishAction from '@src/redux/action/FJPublishAction.ts';
import FJPublishManager from '@src/manager/publish/FJPublishManager.ts';
import FJMessageStore, { MESSAGE_TYPE } from '@src/store/FJMessageStore';
import FJArchiveUtil from '@src/util/FJArchiveUtil';
import { SHARE_BINDING_STATUS, SOCIAL_KEY } from '@src/constants/FJShareSocialConstant.ts';
import FJAddSocialSlice from '@src/redux/slice/modal/FJAddSocialSlice.ts';
import FJPublishHistorySlice from '@src/redux/slice/modal/FJPublishHistorySlice.ts';
import FJRequirementsUtil from '@src/util/FJRequirementsUtil';
import { setLtsDetailsEntrySource, LTS_DETAILS_ENTRY, type LtsDetailsEntry } from '@src/util/FJLongToShortEa';

export type {
    FJDetailsListItem,
    FJDetailsListItemFailed,
    FJDetailsListItemSuccess,
    FJDetailsSubtitleItem,
    FJDetailsVideoData,
} from '../slice/FJDetailsPageSlice';

type RefetchDispatch = ThunkDispatch<RootState, unknown, AnyAction>;

let _shortId = '';

async function refetchRecentTaskList(dispatch: RefetchDispatch, getState: () => RootState): Promise<void> {
    const loaded = getState().startLanding.projects.items.length;
    const limit = Math.max(FJConfig.TASK_LIST_PAGE_SIZE, loaded);
    const page = await FJLongToShortStore.getTaskListPage({ offset: 1, limit });
    if (page != null) {
        dispatch(
            resetProjectsListPage({
                items: page.items,
                nextOffset: page.nextOffset,
                hasMore: page.hasMore,
                total: page.total,
            }),
        );
    }
}

function syncProjectDetailPatch(
    dispatch: RefetchDispatch,
    getState: () => RootState,
    eventId: string,
    detailId: string,
    patch: { subEventId?: string; status?: string },
) {
    let next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
    if (!next) {
        return;
    }
    next = next.map(f => ({
        ...f,
        details:
            f.eventId === eventId
                ? f.details?.map(d => (d.id === detailId ? { ...d, ...patch } : d))
                : f.details,
    }));
    dispatch(setProjects(next));
}

export const FJDetailsPageAction = {
    /**
     * 详情预览「视频源加载失败或长时间无 manager」：用首页同一 ProjectItem + 当前详情列表重新 `FJProjectStore.init`（恢复联网后点重试用）。
     */
    retryDetailsSessionVideoAction: (): AppThunk => async (_dispatch, getState) => {
        const state = getState();
        let projectId: string | null | undefined = FJProjectStore.getProjectId();
        if (!projectId) {
            projectId =
                state.startLanding.projects.selectedProjectId ??
                state.startLanding.projects.items.find(p => p.details?.some(d => d.id === state.detailsPage.selectedId))
                    ?.id;
        }
        if (!projectId) return;
        const base = state.startLanding.projects.items.find(i => i.id === projectId);
        if (!base) return;
        const details = state.detailsPage.items;
        try {
            await FJProjectStore.init({
                ...base,
                details: details.length > 0 ? details : (base.details ?? []),
            });
        } catch {
            /* 仍失败时由详情媒体区继续展示 FJRetry */
        }
    },

    /**
     * 写入详情列表、侧栏标题并打开详情（与 projects / 生成预览共用）。
     * @param item 项目项（含 `details`、`videoUrl` 等）
     * @param needInit 为 true 时调用 `FJProjectStore.init`；为 false 时仅更新 Redux（假定 FileStore 已由其它流程初始化）
     */
    openDetailsAction:
        (item: ProjectItem, needInit: boolean, detailsEntry?: LtsDetailsEntry): AppThunk =>
        async dispatch => {
            const items = structuredClone(item.details);
            const selectedId = items?.[0]?.id ?? '';

            setLtsDetailsEntrySource(detailsEntry ?? LTS_DETAILS_ENTRY.GENERATE_RESULT);

            dispatch(setDetailsProjectTitle(item.title));
            dispatch(setDetailsItems(items ?? []));
            dispatch(selectDetailsItem(selectedId));
            dispatch(setActiveView(FJ_COMMON_VIEW.DETAILS));
            if (needInit) {
                await FJProjectStore.init({ ...item });
            }

            const eventId = item.eventId ?? '';
            const progressItems = items?.filter(i => i.status === GENERATE_STATE.PROCESSING) ?? [];
            await Promise.all(
                progressItems.map(async i => {
                    FJLongToShortStore.trackingTask({
                        eventId: i.subEventId ?? '',
                        callback: async (item: ProjectItem) => {
                            await dispatch(FJDetailsPageAction._handleTrimResult(eventId, i.id, item));
                        },
                    });
                }),
            );
            const pendingItems = items?.filter(i => i.status === GENERATE_STATE.PENDING) ?? [];
            await Promise.all(
                pendingItems.map(async i => {
                    await dispatch(FJDetailsPageAction.trimPendingAction(eventId, i as FJDetailsListItemSuccess));
                }),
            );

            const shorts_number = items?.length ?? 0;
            FJRequirementsUtil.eaLog('long_to_shorts_result_page_view', { shorts_number });
        },

    hideDetailsPageAction: (): AppThunk => (dispatch, getState) => {
        const { activeView } = getState().common;
        if (
            activeView === FJ_COMMON_VIEW.DETAILS &&
            FJBrowserHistoryStore.isCurrentDetails() &&
            FJBrowserHistoryStore.canNavigateBack()
        ) {
            FJBrowserHistoryStore.navigateBack();
            return;
        }
        dispatch(hideDetailsPage());
        dispatch(setActiveView(FJ_COMMON_VIEW.START_LANDING));
        FJBrowserHistoryStore.replaceLanding();
    },

    selectClipAction:
        (id: string): AppThunk =>
        dispatch => {
            dispatch(selectDetailsItem(id));
        },

    setClipSelectionAction:
        (ids: string[]): AppThunk =>
        dispatch => {
            dispatch(setDetailsSelectedClipIdsSlice(ids));
        },

    toggleClipSelectionAction:
        (id: string): AppThunk =>
        dispatch => {
            dispatch(toggleDetailsClipSelectedSlice(id));
        },

    clearClipSelectionAction: (): AppThunk => dispatch => {
        dispatch(clearDetailsClipSelectionSlice());
    },

    requestMultiRemoveClipsAction:
        (ids: string[]): AppThunk =>
        dispatch => {
            const title =
                ids.length > 1
                    ? FJLocalStore._('delete-selected-files').replace('xxx', ids.length.toString())
                    : FJLocalStore._('delete-selected-file').replace('xxx', ids.length.toString());
            dispatch(
                FJModalsAction.showWarningModalAction({
                    title: title,
                    content: FJLocalStore._('this-cannot-be-undone-the-file-will-be-removed-from-your-recent-files'),
                    closeCallback: () => {
                        dispatch(FJDetailsPageAction.applyClipsRemovedAction(ids));
                        dispatch(clearDetailsClipSelectionSlice());
                    },
                    footerDates: [
                        {
                            btnType: FJConstant.WARNING,
                            btnText: FJLocalStore._('delete'),
                            btnCallback: async () => {
                                await dispatch(FJDetailsPageAction.deleteClipsRemoteAction(ids));
                                dispatch(FJModalsAction.completeWarningModalAction());
                            },
                        },
                        {
                            btnType: FJConstant.CANCEL,
                            btnText: FJLocalStore._('cancel'),
                        },
                    ],
                }),
            );
        },

    /**
     * 工具条「已发布」角标：查看当前片段是否已发布（后续可改为跳转作品链接等）。
     */
    publishedClipAction:
        (id: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            const row = getState().detailsPage.items.find(i => i.id === id) as FJDetailsListItemSuccess;
            if (!row) {
                return;
            }
            _shortId = id;
            const publishedList = row.publishedList ?? [];
            dispatch(FJPublishHistorySlice.actions.setHistory(publishedList));
            dispatch(FJPublishHistorySlice.actions.setIsShow(true));
            FJRequirementsUtil.eaLog('posts_history_page_view', {
                project_id: FJProjectStore.getEventId(),
                short_id: id,
                platform: [...new Set(publishedList.map(item => item.type))],
                post_count: publishedList.length,
            });
        },

    openLinkAction:
        (shareUrl: string, type: string, time: string): AppThunk =>
        (_dispatch, getState) => {
            const publishedList = getState().modal.publishHistory.history;
            window.open(shareUrl, '_blank');
            FJRequirementsUtil.eaLog('posts_history_external_link_click', {
                project_id: FJProjectStore.getEventId(),
                short_id: _shortId,
                platform: type,
                post_time: FJUtil.formatIsoTimestamp(time),
                post_count: publishedList.length,
            });
        },

    /**
     * 工具条「发布到社交平台」：导出并走发布流程（与 `exportClipAction` 的 `publish_social` 一致）。
     */
    publishSocialClipAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            // if (!navigator.onLine) {
            //     FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), 'error');
            //     return;
            // }

            const eventId = FJProjectStore.getEventId();
            if (!eventId) {
                return;
            }
            const row = getState().detailsPage.items.find(i => i.id === id) as FJDetailsListItemSuccess;
            if (!row) {
                return;
            }

            const { title, summary, subtitleList, start, end } = row;
            const subtitles = subtitleList.map(i => ({
                text: i.text,
                start_second: i.start,
                end_second: i.end,
                isTranslated: i.isTranslated,
                transcription_text: i.transcriptionText,
                transcription_words: i.transcriptionWords,
            }));
            FJPublishManager.clear();
            FJPublishManager.eventId = eventId;
            FJPublishManager.subId = id;
            FJPublishManager.duration = end - start;
            FJPublishManager.originalData = { title, summary, subtitles: subtitles };

            const isAllUnlinked = FJConfig.SOCIAL_TYPES.every((type: string) => {
                return getState().share[type].linkAccountState === SHARE_BINDING_STATUS.UN_LINK;
            });
            if (isAllUnlinked && !FJRequirementsUtil.isLocalhost()) {
                FJRequirementsUtil.eaLog('add_account_window_page_view', {
                    source: 'publish',
                    project_id: eventId,
                    short_id: id,
                });
                dispatch(FJAddSocialSlice.actions.setIsShow(true));
                return;
            }

            FJRequirementsUtil.eaLog('short_publish_click', {
                project_id: eventId,
                short_id: id,
                viral_score: row.score,
                video_duration: row.end - row.start,
            });

            dispatch(
                FJDetailsPageAction.exportPublishAction({
                    detailId: id,
                    startLoadPublishData: async hasProgress => {
                        // 还原发布数据
                        // await FJPublishManager.restorePublishedData();
                        if (hasProgress) {
                            dispatch(FJModalsAction.updateProgressModalAction({ progress: 97 }));
                        }
                        // 还原本地数据
                        FJPublishManager.restoreData();
                        // 更新未发布或没有数据恢复的平台字段
                        const updateTextTypes: string[] = [];
                        FJConfig.SOCIAL_TYPES.forEach(type => {
                            const target = FJPublishManager.getPublishTarget(type);
                            if (target) {
                                if (!target.description) {
                                    updateTextTypes.push(type);
                                } else if (type === SOCIAL_KEY.YOUTUBE && !target.title) {
                                    updateTextTypes.push(type);
                                }
                            }
                        });
                        if (updateTextTypes.length !== 0) {
                            await FJPublishManager.getTextData(updateTextTypes).catch(() => {});
                        }
                        if (hasProgress) {
                            dispatch(FJModalsAction.updateProgressModalAction({ progress: 100 }));
                            await FJUtil.wait(1);
                        }
                    },
                    onComplete: (blob: Blob | null) => {
                        if (blob) {
                            dispatch(FJPublishAction.initPublishDataAction(blob, row));
                        }
                    },
                }),
            );
        },

    /**
     * 工具条 Cut：当前与 Trim 共用子任务裁剪流程，后续可拆独立参数或时间线。
     */
    cutClipAction:
        (id: string, callback: () => void): AppThunk =>
        async () => {
            try {
                FJRequirementsUtil.eaLog('short_card_open_in_editor_click', {
                    project_id: FJProjectStore.getEventId() ?? '',
                    short_id: id,
                });
                const editAppBlocksData = await FJProjectStore.getEditAppTimeData(id);
                const url_key = FJArchiveUtil.createId(true);
                localStorage.setItem(url_key, JSON.stringify(editAppBlocksData));
                const url = FJUtil.getCurrentUrl() + `editor/app?ai_long_to_shorts_key=${url_key}`;
                window.open(url, '_blank');

                callback?.();
            } catch (e) {
                console.log(e);
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), MESSAGE_TYPE.ERROR);
                callback?.();
            }
        },

    /**
     * 工具条 Trim：与当前片段对齐选中态（侧栏 / 滚动），后续可在此接入裁剪时间线等业务。
     */
    trimClipAction:
        (id: string): AppThunk =>
        (dispatch, getState) => {
            if (getState().modal.cut.isShow) {
                return;
            }

            setLtsDetailsEntrySource(LTS_DETAILS_ENTRY.TRIM);

            const row: any = getState().detailsPage.items.find(i => i.id === id);
            if (!row) {
                return;
            }
            const brock = FJProjectStore.getSession() as ProjectItem;
            const { thumbnailUrl } = brock;
            dispatch(
                FJTrimAction.initAction(id, {
                    title: row.title,
                    start: row.start,
                    end: row.end,
                    thumbnailUrl,
                }),
            );
        },

    applyTrimClipAction:
        (
            id: string,
            payload: { startTime: number; endTime: number; subTitleList: FJDetailsSubtitleItem[] },
        ): AppThunk =>
        async (dispatch, getState) => {
            const brock = FJProjectStore.getSession() as ProjectItem;
            // #exit

            const { startTime, endTime, subTitleList } = payload;
            const { videoUrl, eventId = '', language, genre } = brock;
            const subtitle = subTitleList.map(item => ({
                text: item.text,
                start_second: item.start,
                end_second: item.end,
                is_translated: item.isTranslated,
                transcription_text: item.transcriptionText,
                transcription_words:
                    item.transcriptionWords && item.transcriptionWords.length > 0
                        ? item.transcriptionWords.map(word => ({
                              text: word.text,
                              start_second: word.start,
                              end_second: word.end,
                          }))
                        : [],
            }));
            dispatch(setDetailsItemStatus({ id, status: GENERATE_STATE.PROCESSING }));
            let subEventId = '';
            subEventId = await FJLongToShortStore.startSubTask({
                videoUrl,
                start: startTime,
                end: endTime,
                eventId,
                subId: id,
                language,
                genre,
                subtitle,
                callback: (item: ProjectItem) => {
                    const resolvedSubEventId = subEventId || item.eventId || '';
                    dispatch(
                        FJDetailsPageAction._dispatchTrimSubTaskTerminal(eventId, id, resolvedSubEventId, item),
                    );
                },
            });
            if (!subEventId) {
                return;
            }
            dispatch(updateDetailsItemSubEventId({ id, subEventId }));
            syncProjectDetailPatch(dispatch, getState, eventId, id, {
                subEventId,
                status: GENERATE_STATE.PROCESSING,
            });
        },

    _dispatchTrimSubTaskTerminal:
        (eventId: string, detailId: string, resolvedSubEventId: string, item: ProjectItem): AppThunk =>
        (dispatch, getState) => {
            const detailStatus = item.details?.find(i => i.id === detailId)?.status;
            if (item.status === GENERATE_STATE.SUCCEEDED && detailStatus === GENERATE_STATE.PENDING) {
                dispatch(FJDetailsPageAction._handleTrimResult(eventId, detailId, item));
            } else {
                FJRequirementsUtil.eaLog('short_card_trim_generate_fail', {
                    project_id: eventId,
                    short_id: detailId,
                    fail_reason: 'backend_job_state_failed',
                    error_code: '',
                });
                dispatch(setDetailsItemStatus({ id: detailId, status: GENERATE_STATE.FAILED }));
                if (resolvedSubEventId) {
                    dispatch(updateDetailsItemSubEventId({ id: detailId, subEventId: resolvedSubEventId }));
                }
                syncProjectDetailPatch(dispatch, getState, eventId, detailId, {
                    subEventId: resolvedSubEventId,
                    status: GENERATE_STATE.FAILED,
                });
            }
            dispatch(FJCommonAction.fetchTaskListAction());
        },

    retryClipAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            const row = getState().detailsPage.items.find(i => i.id === id);
            if (!row || row.status !== GENERATE_STATE.FAILED) {
                return;
            }
            const subEventId = 'subEventId' in row ? String(row.subEventId ?? '').trim() : '';
            if (!subEventId) {
                FJMessageStore.addMessage(FJLocalStore._('network-error-please-try-again'), MESSAGE_TYPE.ERROR);
                return;
            }

            dispatch(retryDetailsItem(id));
            const eventId = FJProjectStore.getEventId() ?? '';
            await FJLongToShortStore.regenerateTask({
                eventId: subEventId,
                callback: (taskItem: ProjectItem) => {
                    dispatch(
                        FJDetailsPageAction._dispatchTrimSubTaskTerminal(eventId, id, subEventId, taskItem),
                    );
                },
            });
        },

    _handleTrimResult:
        (eventId: string, detailId: string, resultProjectItem: ProjectItem): AppThunk =>
        async (dispatch, getState) => {
            const detailItem = resultProjectItem.details?.find(d => d.id === detailId);

            if (!detailItem || FJProjectStore.getEventId() !== eventId) {
                return;
            }
            if (detailItem.status === GENERATE_STATE.PENDING) {
                await dispatch(
                    FJDetailsPageAction.trimPendingAction(eventId ?? '', detailItem as FJDetailsListItemSuccess),
                );
                const selectDuration =
                    'start' in detailItem && 'end' in detailItem ? Math.max(0, detailItem.end - detailItem.start) : 0;
                FJRequirementsUtil.eaLog('short_card_trim_generate_success', {
                    project_id: eventId,
                    short_id: detailId,
                    select_duration: selectDuration,
                });
            } else {
                let next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
                if (next) {
                    next = next.map(f => ({
                        ...f,
                        details:
                            f.eventId === eventId
                                ? f.details?.map(d =>
                                      d.id === detailId ? { ...detailItem, status: GENERATE_STATE.FAILED } : d,
                                  )
                                : f.details,
                    }));
                    dispatch(setProjects(next));
                }
                dispatch(setDetailsItemStatus({ ...detailItem, status: GENERATE_STATE.FAILED }));
            }
        },
    trimPendingAction:
        (eventId: string, detailItem: FJDetailsListItemSuccess): AppThunk =>
        async (dispatch, getState) => {
            const { start, end, subtitleList, id, title, score, summary } = detailItem;
            await FJProjectStore.updateDetailData(eventId, id, {
                start,
                end,
                subtitleList,
                title,
                score,
                summary,
            });
            if (eventId !== FJProjectStore.getEventId()) {
                return;
            }
            const block = FJProjectStore.getSession() as ProjectItem;
            const newDetailItem = block.details?.find(d => d.id === id) as FJDetailsListItemSuccess;
            if (FJCoreConfig.isAsyncUpdate) {
                FJLongToShortUpdateStore.uploadSubStatus(eventId, id, GENERATE_STATE.SUCCEEDED);
            } else {
                FJLongToShortUpdateStore.uploadSubInitialData(eventId, newDetailItem);
            }

            const next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (next) {
                dispatch(
                    setProjects(
                        next.map(f => ({
                            ...f,
                            details:
                                f.eventId === eventId
                                    ? f.details?.map(d =>
                                          d.id === id ? { ...newDetailItem, status: GENERATE_STATE.SUCCEEDED } : d,
                                      )
                                    : f.details,
                        })) || [],
                    ),
                );
            }
            dispatch(
                updateDetailsItem({ ...newDetailItem, status: GENERATE_STATE.SUCCEEDED } as FJDetailsListItemSuccess),
            );
        },
    toggleClipLikedAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            const row = getState().detailsPage.items.find(i => i.id === id);
            if (!row || !('liked' in row)) {
                return;
            }
            const nextLiked = !row.liked;
            const eid = FJProjectStore.getEventId();
            if (eid) {
                const result = await FJLongToShortUpdateStore.updateLike(eid, id, nextLiked);
                if (result === undefined) {
                    return;
                }
            }
            FJProjectStore.setClipLiked(id, nextLiked);
            dispatch(toggleDetailsItemLiked(id));
            const nextProjects = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (nextProjects) {
                dispatch(setProjects(nextProjects));
            }
            if (nextLiked) {
                FJRequirementsUtil.eaLog('short_card_like_click', {
                    project_id: eid ?? '',
                    short_id: id,
                });
            }
        },

    /**
     * 点踩入口：当当前条目未被点踩时弹出「不喜欢原因」弹窗，否则直接切换点踩状态。
     * 这样可以保证「取消点踩」不会重复弹出原因选择。
     */
    toggleClipDislikedAction:
        (id: string): AppThunk =>
        async (dispatch, getState) => {
            const row = getState().detailsPage.items.find(i => i.id === id);
            if (row && 'disliked' in row && row.disliked) {
                const eid = FJProjectStore.getEventId();
                if (eid) {
                    const result = await FJLongToShortUpdateStore.updateDislike(eid, id, false);
                    if (result === undefined) {
                        return;
                    }
                }
                FJProjectStore.setClipDisliked(id, false);
                dispatch(toggleDetailsItemDisliked(id));
                const nextProjects = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
                if (nextProjects) {
                    dispatch(setProjects(nextProjects));
                }
                return;
            }
            FJRequirementsUtil.eaLog('short_card_dislike_click', {
                project_id: FJProjectStore.getEventId() ?? '',
                short_id: id,
            });
            dispatch(FJModalsAction.showDislikeModalAction({ targetDetailId: id }));
        },

    /** 不喜欢原因弹窗提交：埋点占位 + 本地点踩 + 同步服务端 */
    submitClipDislikeReasonsAction:
        (payload: { id: string; keys: string[] }): AppThunk =>
        async (dispatch, getState) => {
            const { id, keys } = payload;
            const feedback = keys.join(',');
            const eid = FJProjectStore.getEventId();
            FJRequirementsUtil.eaLog('short_card_dislike_reason_submit', {
                project_id: eid ?? '',
                short_id: id,
                reason_id: keys.join(',') || '/',
            });
            if (eid) {
                const result = await FJLongToShortUpdateStore.updateDislike(eid, id, true, feedback);
                if (result === undefined) {
                    return;
                }
            }
            FJProjectStore.setClipDisliked(id, true);
            dispatch(toggleDetailsItemDisliked(id));
            const nextProjects = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (nextProjects) {
                dispatch(setProjects(nextProjects));
            }
        },

    /** 侧栏 More → 重命名：弹出重命名框（确认后在 `applyRenameClipAction` 写核心数据 + Redux） */
    requestRenameClipAction:
        (id: string): AppThunk =>
        (dispatch, getState) => {
            const row = getState().detailsPage.items.find(i => i.id === id);
            if (!row) {
                return;
            }
            FJRequirementsUtil.eaLog('short_card_rename_click', {
                project_id: FJProjectStore.getEventId() ?? '',
                short_id: id,
                title_content: row.title ?? '',
            });
            dispatch(
                FJModalsAction.showRenameModalAction({
                    initialTitle: row.title,
                    context: 'details-clip',
                    targetId: id,
                }),
            );
        },

    /** 重命名弹窗确认：有 `eventId` 时先同步服务端子块标题，再写本地与 Redux + 最近文件 */
    applyRenameClipAction:
        (payload: { id: string; title: string }): AppThunk =>
        async (dispatch, getState) => {
            const { id, title } = payload;
            const t = title.trim();
            if (!t) {
                return;
            }
            const prevRow = getState().detailsPage.items.find(i => i.id === id);
            const title_content = prevRow?.title ?? '';
            const eid = FJProjectStore.getEventId();
            if (eid) {
                const result = await FJLongToShortUpdateStore.renameSubBlock(eid, id, t);
                if (result === undefined) {
                    return;
                }
            }
            FJRequirementsUtil.eaLog('short_card_rename_confirm_click', {
                project_id: eid ?? '',
                short_id: id,
                title_content,
                new_title_content: t,
            });
            dispatch(FJModalsAction.hideRenameModalAction());
            FJProjectStore.setClipTitle(id, t);
            dispatch(renameDetailsItem({ id, title: t }));
            const next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (next) {
                dispatch(setProjects(next));
            }
        },

    /** More → 删除：确认后先 `FJProjectStore.removeClip` 再更新详情列表与项目列表 */
    requestRemoveClipAction:
        (id: string): AppThunk =>
        (dispatch, getState) => {
            void getState();
            FJRequirementsUtil.eaLog('short_card_delete_click', {
                project_id: FJProjectStore.getEventId() ?? '',
                short_id: id,
            });
            dispatch(
                FJModalsAction.showWarningModalAction({
                    title: FJLocalStore._('delete-this-clip'),
                    content: FJLocalStore._(
                        'this-clip-will-be-removed-from-the-current-project-you-can-generate-it-again-later',
                    ),
                    closeCallback: () => {
                        dispatch(FJDetailsPageAction.applyClipsRemovedAction([id]));
                    },
                    footerDates: [
                        {
                            btnType: FJConstant.WARNING,
                            btnText: FJLocalStore._('delete'),
                            btnCallback: async () => {
                                await dispatch(FJDetailsPageAction.deleteClipsRemoteAction([id]));
                                dispatch(FJModalsAction.completeWarningModalAction());
                            },
                        },
                        {
                            btnType: FJConstant.CANCEL,
                            btnText: FJLocalStore._('cancel'),
                        },
                    ],
                }),
            );
        },

    deleteClipsRemoteAction:
        (subIds: string[]): AppThunk =>
        async () => {
            const eid = FJProjectStore.getEventId();
            const ids = Array.isArray(subIds) ? subIds : [subIds];
            if (!eid) {
                return;
            }
            const { code } = await FJLongToShortUpdateStore.deleteSubBlock(eid, [...ids]);
            if (code !== 200) {
                throw new Error('delete_sub_block_failed');
            }
        },

    applyClipsRemovedAction:
        (subIds: string[]): AppThunk =>
        async (dispatch, getState) => {
            const eid = FJProjectStore.getEventId();
            const ids = Array.isArray(subIds) ? subIds : [subIds];
            FJRequirementsUtil.eaLog('short_card_delete_confirm', { project_id: eid ?? '', short_id: ids.join(',') });
            for (const id of ids) {
                FJProjectStore.removeClip(id);
                dispatch(removeDetailsItem(id));
            }

            const next = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
            if (next) {
                dispatch(setProjects(next));
            }
            await refetchRecentTaskList(dispatch, getState);
        },

    removeClipsAction:
        (subIds: string[]): AppThunk =>
        async dispatch => {
            await dispatch(FJDetailsPageAction.deleteClipsRemoteAction(subIds));
            await dispatch(FJDetailsPageAction.applyClipsRemovedAction(subIds));
        },
    handleDownloadAction:
        (detailId: string, callback: () => void): AppThunk =>
        async dispatch => {
            FJRequirementsUtil.eaLog('short_card_download_click', {
                project_id: FJProjectStore.getEventId() ?? '',
                short_id: detailId,
            });
            dispatch(
                FJDetailsPageAction.exportClipAction({
                    detailId,
                    messageKey: 'downloading',
                    onComplete: (blob: Blob | null) => {
                        const eid = FJProjectStore.getEventId() ?? '';
                        if (blob) {
                            const fileName = FJUtil.getUseFileName(undefined, '.mp4');
                            const mimeType = 'video/mp4';
                            Download(blob, fileName, mimeType);
                            FJRequirementsUtil.eaLog('short_card_download_success', {
                                project_id: eid,
                                short_id: detailId,
                            });
                        } else {
                            FJRequirementsUtil.eaLog('short_card_download_fail', {
                                project_id: eid,
                                short_id: detailId,
                                fail_reason: 'no_blob',
                            });
                        }
                        callback?.();
                    },
                }),
            );
        },

    exportClipAction:
        (payload: {
            detailId: string;
            messageKey: 'downloading' | 'publishing';
            onComplete?: (blob: Blob | null) => void;
        }): AppThunk =>
        async (dispatch, getState) => {
            const { detailId, messageKey, onComplete } = payload;
            const eid = () => FJProjectStore.getEventId() ?? '';
            const videoBlob = await FJProjectStore.getExportedBlob(detailId);
            if (videoBlob) {
                onComplete?.(videoBlob);
                return;
            }
            const mgr = FJProjectStore.getManager(detailId);
            if (!mgr) {
                FJMessageStore.addMessage(
                    FJLocalStore._('the-file-could-not-be-downloaded-please-try-again'),
                    MESSAGE_TYPE.ERROR,
                );
                if (messageKey === 'downloading') {
                    FJRequirementsUtil.eaLog('short_card_download_fail', {
                        project_id: eid(),
                        short_id: detailId,
                        fail_reason: 'no_export_manager',
                    });
                }
                onComplete?.(null);
                return;
            }
            if (FJExportStore.getIsExporting()) {
                onComplete?.(null);
                return;
            }

            onComplete?.(null);

            dispatch(
                FJModalsAction.showProgressModalAction(
                    {
                        messageKey,
                        hint: FJLocalStore._('please-dont-close-this-window'),
                        progress: 0,
                    },
                    () => {
                        const p = getState().modals.progressModalData.progress;
                        if (messageKey === 'downloading') {
                            FJRequirementsUtil.eaLog('download_modal_close', { progress: p });
                        }
                        if (FJExportStore.getIsExporting()) {
                            FJExportStore.stopExport();
                        }
                    },
                ),
            );

            mgr.export({
                onProgress: (p: number) => {
                    const n = Number(p) * 100;
                    const pct = Math.min(100, Math.max(0, Math.round(n)));
                    dispatch(FJModalsAction.updateProgressModalAction({ progress: pct }));
                },
                onComplete: async (blob: Blob) => {
                    dispatch(FJModalsAction.hideProgressModalAction());
                    if (blob) {
                        await FJProjectStore.saveExportedBlob(detailId, blob);
                        const nextProjects = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
                        if (nextProjects) {
                            dispatch(setProjects(nextProjects));
                        }
                    }
                    onComplete?.(blob);
                },
                onError: (_err: unknown) => {
                    dispatch(FJModalsAction.hideProgressModalAction());

                    FJMessageStore.addMessage(
                        FJLocalStore._('the-file-could-not-be-downloaded-please-try-again'),
                        MESSAGE_TYPE.ERROR,
                    );
                    if (messageKey === 'downloading') {
                        FJRequirementsUtil.eaLog('short_card_download_fail', {
                            project_id: eid(),
                            short_id: detailId,
                            fail_reason: 'export_error',
                        });
                    }
                    onComplete?.(null);
                },
            });
        },
    exportPublishAction:
        (payload: {
            detailId: string;
            onComplete?: (blob: Blob | null) => void;
            startLoadPublishData: (hasProgress: boolean) => Promise<void>;
        }): AppThunk =>
        async (dispatch, getState) => {
            const { detailId, onComplete, startLoadPublishData } = payload;
            const mgr = FJProjectStore.getManager(detailId);
            let __isShowLoadingModal = false;
            const __showLoadingModal = () => {
                if (!__isShowLoadingModal) {
                    dispatch(FJModalsAction.showLoadingModalAction(FJLocalStore._('loading'), undefined, true));
                    __isShowLoadingModal = true;
                }
            };
            const __hideLoadingModal = () => {
                if (__isShowLoadingModal) {
                    dispatch(FJModalsAction.hideLoadingModalAction());
                    __isShowLoadingModal = false;
                }
            };
            __showLoadingModal();
            const persistedBlob = await FJProjectStore.getExportedBlob(detailId);
            let videoBlob = persistedBlob;
            if (!videoBlob && mgr?._exportBlob) {
                videoBlob = mgr._exportBlob;
            }

            if (videoBlob) {
                __showLoadingModal();
                await startLoadPublishData(false).catch(() => {});
                __hideLoadingModal();
                onComplete?.(videoBlob);
                return;
            }

            if (!mgr) {
                FJMessageStore.addMessage(FJLocalStore._('this-clip-is-not-ready-to-export'), MESSAGE_TYPE.ERROR);
                onComplete?.(null);
                __hideLoadingModal();
                return;
            }
            if (FJExportStore.getIsExporting()) {
                __hideLoadingModal();
                onComplete?.(null);
                return;
            }
            // 预加载
            await mgr.getVideoElement().getSourceBlob();
            __hideLoadingModal();

            dispatch(
                FJModalsAction.showProgressModalAction(
                    {
                        messageKey: 'loading',
                        hint: FJLocalStore._('please-dont-close-this-window'),
                        progress: 0,
                    },
                    () => {
                        FJRequirementsUtil.eaLog('short_publish_progess_window_close');
                        if (FJExportStore.getIsExporting()) {
                            FJExportStore.stopExport();
                        }
                    },
                ),
            );

            mgr.export({
                onProgress: (p: number) => {
                    const n = Number(p) * 100 * 0.95;
                    const pct = Math.min(100, Math.max(0, Math.round(n)));
                    dispatch(FJModalsAction.updateProgressModalAction({ progress: pct }));
                },
                onComplete: async (blob: Blob) => {
                    await startLoadPublishData(true).catch(() => {});
                    dispatch(FJModalsAction.hideProgressModalAction());
                    if (blob) {
                        await FJProjectStore.saveExportedBlob(detailId, blob);
                        const nextProjects = FJProjectStore.mergeRecent(getState().startLanding.projects.items);
                        if (nextProjects) {
                            dispatch(setProjects(nextProjects));
                        }
                    }
                    onComplete?.(blob);
                },
                onError: () => {
                    dispatch(FJModalsAction.hideProgressModalAction());

                    FJMessageStore.addMessage(
                        FJLocalStore._('video-export-failed-unable-to-publish-to-selected-platform-please-try-again'),
                        MESSAGE_TYPE.ERROR,
                    );
                    onComplete?.(null);
                },
            });
        },
} as const;
